import * as React from 'react';
import { connect } from 'react-redux';
import { isEmpty } from 'lodash';
import moment from 'moment';

import { withScrollTo } from 'components/ScrollToHOC';

import CampaignPayoutsHeader from './header/CampaignPayoutsHeader';
import CampaignPayoutsList from './list/CampaignPayoutsList';

import {
  fetchPayoutsByCampaign,
  resetPayoutsByCampaign,
  unreleasePartialPayout
} from 'modules/campaigns/reducers/payouts/campaign-payouts';
import { createVariablePayments, editVariablePayments } from 'modules/campaigns/reducers/variable-payments/variable-payments';
import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';

import { isNegotiationsReopened, isOther } from 'utils/subcampaign';
import {
  getPaymentInfo,
  getTotalVariablePayment,
  getUnallocatedAmount,
  getVariablePaymentsById
} from 'utils/payouts';

import './campaign-payouts.scss';

const CampaignPayoutsHeaderWithScrollTo = withScrollTo(CampaignPayoutsHeader);

import type {
  Campaign,
  CampaignOverview,
  PaymentConfirmationModalParams,
  PaymentsLoading,
  Payout,
  Subcampaign,
  VariablePayment,
  VariablePayments
} from 'types';

type OwnProps = {
  campaign: Campaign,
  subcampaignsList: Array<Subcampaign>,
  onVariablePaymentsUpdate: () => void
};

type StateProps = {
  payoutsByCampaign: Array<Payout>,
  variablePayments: VariablePayments,
  isLoading: PaymentsLoading,
  overview: CampaignOverview
};

type DispatchProps = {
  dispatchFetchPayoutsByCampaign: (campaignId: number) => void,
  dispatchResetPayoutsByCampaign: () => void,
  dispatchCreateVariablePayments: (
    campaignId: number,
    subcampaignId: number,
    variablePayments: Array<VariablePayment>
  ) => Promise<any>,
  dispatchEditVariablePayments: (
    campaignId: number,
    subcampaignId: number,
    variablePayments: Array<VariablePayment>
  ) => Promise<any>,
  dispatchUnreleasePartialPayout: (
    payoutId: number,
    campaignId: number,
    subcampaignId: number
  ) => void,
  dispatchShowConfirmationModal: (params: PaymentConfirmationModalParams) => void,
  dispatchHideConfirmationModal: () => void
};

type Props = OwnProps & StateProps & DispatchProps;

export class CampaignPayouts extends React.PureComponent<Props> {
  componentDidMount() {
    const { campaign, onVariablePaymentsUpdate, dispatchFetchPayoutsByCampaign } = this.props;

    dispatchFetchPayoutsByCampaign(campaign.id);

    onVariablePaymentsUpdate();
  }

  componentWillUnmount() {
    const { dispatchResetPayoutsByCampaign } = this.props;

    dispatchResetPayoutsByCampaign();
  }

  shouldPayoutsListBeShown(
    payouts: Array<Payout>,
    variablePayments: Array<VariablePayment>,
    isTotalPayout: boolean,
    otherArrangement: boolean,
    hasPayoutInstallments: boolean
  ): boolean {
    if (!isEmpty(variablePayments) || hasPayoutInstallments) {
      return true;
    }

    if (!isEmpty(payouts) && !isTotalPayout && !otherArrangement) {
      return true;
    }

    return false;
  }

  getPayoutsBySubcampaignId(subcampaignId: number): Array<Payout> {
    const { payoutsByCampaign } = this.props;

    const subcampaignPayouts = payoutsByCampaign.filter(payout => (
      payout.subcampaignId === subcampaignId
    ));

    return subcampaignPayouts.sort((payout1, payout2) =>
      moment(payout2.releaseDate).unix() - moment(payout1.releaseDate).unix());
  }

  getVariablePaymentsByInfluencerId(subcampaign: Subcampaign): Array<VariablePayment> {
    const { variablePayments } = this.props;
    const creatorProfile = subcampaign.creatorProfile || {};
    const updatedVariablePayments = variablePayments.items || [];

    if (isEmpty(creatorProfile) || isEmpty(updatedVariablePayments)) {
      return [];
    }

    return getVariablePaymentsById('influencerProfileId', creatorProfile.id, updatedVariablePayments);
  }

  handleCreateVariablePayments = (subcampaignId: number, variablePayments: Array<VariablePayment>): void => {
    const { campaign, onVariablePaymentsUpdate, dispatchCreateVariablePayments } = this.props;
    const campaignId = (campaign && campaign.id) || null;

    if (campaignId) {
      dispatchCreateVariablePayments(campaignId, subcampaignId, variablePayments)
        .then(() => onVariablePaymentsUpdate());
    }
  };

  handleEditVariablePayments = (subcampaignId: number, variablePayments: Array<VariablePayment>): void => {
    const { campaign, onVariablePaymentsUpdate, dispatchEditVariablePayments } = this.props;
    const campaignId = (campaign && campaign.id) || null;

    if (campaignId) {
      dispatchEditVariablePayments(campaignId, subcampaignId, variablePayments)
        .then(() => onVariablePaymentsUpdate());
    }
  };

  handleUnreleasePartialPayout = (
    payoutId: number,
    campaignId: number,
    subcampaignId: number
  ): void => {
    const { dispatchUnreleasePartialPayout } = this.props;

    dispatchUnreleasePartialPayout(payoutId, campaignId, subcampaignId);
  };

  renderPayout = (subcampaign: Subcampaign): JSX.Element => {
    const { campaign, overview, isLoading, dispatchShowConfirmationModal, dispatchHideConfirmationModal } = this.props;
    const campaignOverview = overview || {};

    const campaignBudgetInfo = {
      total: campaign.budget,
      remaining: campaignOverview.remainingBudget,
      used: campaignOverview.usedBudget
    };

    const payouts = this.getPayoutsBySubcampaignId(subcampaign.id);
    const variablePayments = this.getVariablePaymentsByInfluencerId(subcampaign);
    const isTotalPayout = payouts.length && payouts.some(item => item.isTotalPayout);
    const totalPayoutExecuted = isTotalPayout ? payouts[0].executed : false;
    const releaseDate = isTotalPayout ? payouts[0].releaseDate : '';
    const originalReleaseDate = isTotalPayout ? payouts[0].originalReleaseDate : '';
    const otherArrangement = isOther(subcampaign);
    const paymentInfo = getPaymentInfo(subcampaign, otherArrangement, variablePayments);
    const totalVariablePayment = getTotalVariablePayment(paymentInfo);
    const unallocatedAmount = getUnallocatedAmount(payouts, subcampaign, paymentInfo);
    const unpaidInstallments = payouts.filter(payout => !payout.releaseDate) || (unallocatedAmount > 0 && totalVariablePayment > 0);
    const hasUnpaidInstallments = unpaidInstallments.length > 0 || (unallocatedAmount > 0 && totalVariablePayment > 0);
    const hasPayoutInstallments = !isEmpty(payouts) || hasUnpaidInstallments;
    const showPayoutsList = this.shouldPayoutsListBeShown(payouts, variablePayments, Boolean(isTotalPayout), otherArrangement, hasPayoutInstallments);
    const isNegotiationReopened = isNegotiationsReopened(subcampaign);

    return (
      <div
        key={`payouts-section-${subcampaign.id}`}
        className="campaign-payouts__section"
      >
        <CampaignPayoutsHeaderWithScrollTo
          hash={`payouts-${subcampaign.id}`}
          campaign={campaign}
          subcampaign={subcampaign}
          campaignBudgetInfo={campaignBudgetInfo}
          paymentInfo={paymentInfo}
          unallocatedAmount={unallocatedAmount}
          variablePayments={variablePayments}
          hasPayoutInstallments={hasPayoutInstallments}
          hasUnpaidPayoutInstallments={hasUnpaidInstallments}
          isTotalPayout={Boolean(isTotalPayout)}
          totalPayoutExecuted={totalPayoutExecuted}
          isNegotiationReopened={isNegotiationReopened}
          releaseDate={releaseDate}
          originalReleaseDate={originalReleaseDate}
          otherArrangement={otherArrangement}
          isLoading={isLoading}
          onCreateVariablePayments={this.handleCreateVariablePayments}
          onEditVariablePayments={this.handleEditVariablePayments}
        />
        {
          showPayoutsList && (
            <CampaignPayoutsList
              payouts={payouts}
              paymentInfo={paymentInfo}
              campaign={campaign}
              subcampaign={subcampaign}
              isNegotiationReopened={isNegotiationReopened}
              otherArrangement={otherArrangement}
              onUnreleasePartialPayout={this.handleUnreleasePartialPayout}
              onShowConfirmationModal={dispatchShowConfirmationModal}
              onHideConfirmationModal={dispatchHideConfirmationModal}
            />
          )
        }
      </div>
    );
  };

  render(): React.ReactNode {
    const { subcampaignsList } = this.props;

    return (
      <section
        className="campaign-payouts"
        data-qa="campaign-payouts"
      >
        {
          subcampaignsList.map(this.renderPayout)
        }
      </section>
    );
  }
}

const mapStateToProps = (
  { campaignPayouts, campaignVariablePayments, campaignOverview }
): StateProps => ({
  payoutsByCampaign: campaignPayouts.payoutsByCampaign,
  variablePayments: campaignVariablePayments.variablePayments,
  overview: campaignOverview.overview,

  isLoading: {
    payouts: campaignPayouts.isLoading,
    variablePayments: campaignVariablePayments.isLoading
  }
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchPayoutsByCampaign: (campaignId: number): void => dispatch(fetchPayoutsByCampaign(campaignId)),
  dispatchResetPayoutsByCampaign: (): void => dispatch(resetPayoutsByCampaign()),

  dispatchCreateVariablePayments: (campaignId: number, subcampaignId: number, variablePayments: Array<VariablePayment>) => (
    dispatch(createVariablePayments(campaignId, subcampaignId, variablePayments))
  ),

  dispatchEditVariablePayments: (campaignId: number, subcampaignId: number, variablePayments: Array<VariablePayment>) => (
    dispatch(editVariablePayments(campaignId, subcampaignId, variablePayments))
  ),
  dispatchUnreleasePartialPayout: (payoutId: number, campaignId: number, subcampaignId: number) => (
    dispatch(unreleasePartialPayout(payoutId, campaignId, subcampaignId))
  ),
  dispatchShowConfirmationModal: (params: PaymentConfirmationModalParams) => (
    dispatch(showConfirmModal(params))
  ),
  dispatchHideConfirmationModal: () => (
    dispatch(hideConfirmModal())
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(CampaignPayouts);
